import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, MessageCircle, Target, ArrowRight } from "lucide-react";

interface AnalyticsData {
  totalSessions: number;
  totalMessages: number;
  routedToContact: number;
  popularIntents: { intent: string; count: number }[];
  sessionsOverTime: { date: string; count: number }[];
}

const COLORS = ['hsl(var(--ministry-purple))', 'hsl(var(--ministry-gold))', 'hsl(var(--ministry-rose))', 'hsl(var(--ministry-emerald))', 'hsl(var(--ministry-cyan))'];

export const ChatSessionsAnalytics = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSessions: 0,
    totalMessages: 0,
    routedToContact: 0,
    popularIntents: [],
    sessionsOverTime: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      // Total sessions
      const { count: sessionsCount } = await supabase
        .from("chat_sessions")
        .select("*", { count: "exact", head: true });

      // Total messages
      const { count: messagesCount } = await supabase
        .from("chat_messages")
        .select("*", { count: "exact", head: true });

      // Routed to contact
      const { count: routedCount } = await supabase
        .from("chat_sessions")
        .select("*", { count: "exact", head: true })
        .eq("routed_to_contact", true);

      // Popular intents
      const { data: intentData } = await supabase
        .from("chat_messages")
        .select("intent")
        .not("intent", "is", null);

      const intentCounts: Record<string, number> = {};
      (intentData || []).forEach((msg) => {
        if (msg.intent) {
          intentCounts[msg.intent] = (intentCounts[msg.intent] || 0) + 1;
        }
      });

      const popularIntents = Object.entries(intentCounts)
        .map(([intent, count]) => ({ intent, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Sessions over time (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: sessionsData } = await supabase
        .from("chat_sessions")
        .select("created_at")
        .gte("created_at", thirtyDaysAgo.toISOString());

      const dateCounts: Record<string, number> = {};
      (sessionsData || []).forEach((session) => {
        const date = new Date(session.created_at).toLocaleDateString();
        dateCounts[date] = (dateCounts[date] || 0) + 1;
      });

      const sessionsOverTime = Object.entries(dateCounts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setAnalytics({
        totalSessions: sessionsCount || 0,
        totalMessages: messagesCount || 0,
        routedToContact: routedCount || 0,
        popularIntents,
        sessionsOverTime,
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading analytics...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Chat Sessions</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalSessions}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalMessages}</div>
            <p className="text-xs text-muted-foreground">Bot + User messages</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Routed to Contact</CardTitle>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.routedToContact}</div>
            <p className="text-xs text-muted-foreground">
              {analytics.totalSessions > 0
                ? `${((analytics.routedToContact / analytics.totalSessions) * 100).toFixed(1)}% of sessions`
                : "No sessions yet"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Queries Answered</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.totalSessions - analytics.routedToContact}
            </div>
            <p className="text-xs text-muted-foreground">By chatbot directly</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Popular Intents */}
        <Card>
          <CardHeader>
            <CardTitle>Popular Intents</CardTitle>
            <CardDescription>Top 5 most used chatbot features</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.popularIntents.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analytics.popularIntents}
                    dataKey="count"
                    nameKey="intent"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={(entry) => entry.intent}
                  >
                    {analytics.popularIntents.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">No intent data available</p>
            )}
          </CardContent>
        </Card>

        {/* Sessions Over Time */}
        <Card>
          <CardHeader>
            <CardTitle>Sessions Over Time</CardTitle>
            <CardDescription>Last 30 days activity</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.sessionsOverTime.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analytics.sessionsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="hsl(var(--primary))" name="Sessions" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-8">No session data for the last 30 days</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
